import { useEffect } from 'react';
import { Link } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Clock, Download, ExternalLink, MapPin } from 'lucide-react';

import { BROWSER_TZ, CATEGORY_LABELS, shortTzLabel, type CalendarEntry } from './entry-card';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { formatSessionTime } from '@/lib/geography';
import { fetchJson } from '@/lib/queryClient';

function buildGoogleCalendarUrl(entry: CalendarEntry): string {
  const fmtUtc = (d: Date) =>
    d
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}/, '');
  const start = fmtUtc(new Date(entry.startsAt));
  const end = entry.endsAt ? fmtUtc(new Date(entry.endsAt)) : start;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: entry.title,
    dates: `${start}/${end}`,
  });
  const desc = entry.description ? entry.description.replace(/<[^>]+>/g, '') : '';
  if (desc) params.set('details', desc);
  const loc = entry.venueName ?? entry.sourceName ?? entry.creatorName;
  if (loc) params.set('location', loc);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * The canonical full-detail view for a single calendar entry — shared by the
 * region/locality calendar and any venue-scoped agenda so "read more" behaves
 * identically everywhere. Owns the entry-detail fetch, JSON-LD injection, and
 * add-to-calendar affordances; the caller only owns which entry (if any) is open.
 */
export function EventDetailSheet({
  entryId,
  onOpenChange,
}: {
  entryId: string | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: eventDetailResponse } = useQuery<{ data: CalendarEntry }>({
    queryKey: ['calendar-entry-detail', entryId],
    queryFn: () => fetchJson<{ data: CalendarEntry }>(`/api/calendar/entries/${entryId}`),
    enabled: !!entryId,
    staleTime: 1000 * 60 * 10,
  });

  // Inject JSON-LD structured data while the detail sheet is open
  useEffect(() => {
    if (!eventDetailResponse) return;
    const d = eventDetailResponse.data;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'event-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: d.title,
      startDate: d.startsAt,
      endDate: d.endsAt ?? d.startsAt,
      description: d.description?.replace(/<[^>]+>/g, '') ?? undefined,
      location: d.venueName ?? d.sourceName ?? d.creatorName ?? undefined,
      eventStatus:
        d.eventStatus === 'cancelled'
          ? 'https://schema.org/EventCancelled'
          : d.eventStatus === 'postponed'
            ? 'https://schema.org/EventPostponed'
            : 'https://schema.org/EventScheduled',
      eventAttendanceMode:
        d.attendanceMode === 'online'
          ? 'https://schema.org/OnlineEventAttendanceMode'
          : d.attendanceMode === 'hybrid'
            ? 'https://schema.org/MixedEventAttendanceMode'
            : 'https://schema.org/OfflineEventAttendanceMode',
      url: d.externalUrl ?? `${window.location.origin}/events/${d.id}`,
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById('event-jsonld')?.remove();
    };
  }, [eventDetailResponse]);

  return (
    <Sheet open={!!entryId} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
        {entryId && !eventDetailResponse ? (
          <div className="flex h-40 items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : eventDetailResponse ? (
          <>
            <SheetHeader className="pb-4">
              <div className="flex flex-wrap gap-2 mb-2">
                <Badge
                  variant={eventDetailResponse.data.kind === 'session' ? 'default' : 'secondary'}
                  className="rounded-full px-3 py-1"
                >
                  {eventDetailResponse.data.kind === 'session'
                    ? 'Class'
                    : eventDetailResponse.data.category
                      ? (CATEGORY_LABELS[eventDetailResponse.data.category] ?? 'Event')
                      : 'Event'}
                </Badge>
                {eventDetailResponse.data.attendanceMode &&
                eventDetailResponse.data.attendanceMode !== 'in_person' ? (
                  <Badge variant="outline" className="rounded-full px-3 py-1">
                    {eventDetailResponse.data.attendanceMode === 'hybrid' ? 'Hybrid' : 'Online'}
                  </Badge>
                ) : null}
              </div>
              <SheetTitle className="text-2xl font-semibold leading-snug">
                {eventDetailResponse.data.title}
              </SheetTitle>
            </SheetHeader>

            <div className="space-y-5 pt-1">
              <div className="flex items-start gap-3 text-sm">
                <CalendarDays className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
                <div className="flex flex-wrap items-center gap-2">
                  <span>
                    {formatSessionTime(
                      eventDetailResponse.data.startsAt,
                      eventDetailResponse.data.endsAt ?? eventDetailResponse.data.startsAt,
                      eventDetailResponse.data.timezone
                    )}
                  </span>
                  {eventDetailResponse.data.timezone &&
                  eventDetailResponse.data.timezone !== BROWSER_TZ ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/60 px-2 py-0.5 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      Times shown in {shortTzLabel(eventDetailResponse.data.timezone)}
                    </span>
                  ) : null}
                </div>
              </div>

              {(eventDetailResponse.data.venueName ??
              eventDetailResponse.data.sourceName ??
              eventDetailResponse.data.creatorName) ? (
                <div className="flex items-start gap-3 text-sm">
                  <MapPin className="h-5 w-5 mt-0.5 text-muted-foreground shrink-0" />
                  <span>
                    {eventDetailResponse.data.venueName ??
                      eventDetailResponse.data.sourceName ??
                      eventDetailResponse.data.creatorName}
                  </span>
                </div>
              ) : null}

              {eventDetailResponse.data.description ? (
                <p className="text-sm leading-7 text-muted-foreground">
                  {eventDetailResponse.data.description.replace(/<[^>]+>/g, '')}
                </p>
              ) : null}

              <div className="border-t border-border/70 pt-5 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Add to calendar
                </p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={buildGoogleCalendarUrl(eventDetailResponse.data)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" className="rounded-full px-5 gap-2">
                      <ExternalLink className="h-4 w-4" />
                      Google Calendar
                    </Button>
                  </a>
                  <a href={`/api/calendar/entries/${eventDetailResponse.data.id}.ics`} download>
                    <Button variant="outline" className="rounded-full px-5 gap-2">
                      <Download className="h-4 w-4" />
                      Download .ics
                    </Button>
                  </a>
                </div>
              </div>

              {eventDetailResponse.data.kind === 'session' && eventDetailResponse.data.classId ? (
                <Link
                  href={`/classes/${eventDetailResponse.data.classId}`}
                  onClick={() => onOpenChange(false)}
                >
                  <Button className="w-full rounded-full">View class</Button>
                </Link>
              ) : null}
              {eventDetailResponse.data.kind === 'event' && eventDetailResponse.data.externalUrl ? (
                <a
                  href={eventDetailResponse.data.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button className="w-full rounded-full gap-2">
                    Learn more
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </a>
              ) : null}
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
